import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Download, Play, RefreshCw, Square } from 'lucide-react';
import { T, feldStil, knopfStil } from '../../lib/designTokens';

// Kundenbenennung Vertrieb (api/_lib/dealBenennung.js, seit 24.10.2026): Test eines
// Deals, Trockenlauf über den Bestand und das Umbenennen.
//
// Der Trockenlauf plant alle Deals der Pipeline „Vertrieb“ in einem Aufruf (die
// Reihenfolge entscheidet, wer bei gleichem „Nachname - Ort“ den Zusatz bekommt).
// Umbenannt wird danach in Paketen zu 25, in derselben Reihenfolge, mit Fortschritt
// und Abbrechen nach dem laufenden Paket. Der Server benennt nur um, was heute genau
// so herauskommt wie im Trockenlauf; sonst „Neu prüfen“.

const PAKET = 25;
const ZEILEN_ANFANG = 200;

const STATUS = {
  bereit: { text: 'Wird umbenannt', ton: 'gut' },
  umbenannt: { text: 'Umbenannt', ton: 'gut' },
  passt: { text: 'Passt schon', ton: 'leise' },
  fehlt: { text: 'Daten fehlen', ton: 'warn' },
  pruefen: { text: 'Neu prüfen', ton: 'warn' },
  uebersprungen: { text: 'Übersprungen', ton: 'leise' },
  fehler: { text: 'Fehler', ton: 'gefahr' },
};

const TON = {
  gut: { farbe: T.gut, grund: T.gutGrund, rand: T.gutRand },
  warn: { farbe: T.warn, grund: '#FEF3C7', rand: '#FCD34D' },
  gefahr: { farbe: T.gefahr, grund: T.gefahrGrund, rand: T.gefahrRand },
  leise: { farbe: '#64748B', grund: '#F1F5F9', rand: '#E2E8F0' },
};

function Marke({ status }) {
  const art = STATUS[status] || { text: status, ton: 'leise' };
  const ton = TON[art.ton];
  return (
    <span style={{
      display: 'inline-block', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 700,
      color: ton.farbe, background: ton.grund, border: `1px solid ${ton.rand}`,
      borderRadius: 999, padding: '2px 8px',
    }}>{art.text}</span>
  );
}

function zaehle(eintraege) {
  return eintraege.reduce((summe, e) => ({ ...summe, [e.status]: (summe[e.status] || 0) + 1 }), {});
}

function zusammenfassung(zaehler) {
  return [
    ['bereit', 'werden umbenannt'], ['umbenannt', 'umbenannt'], ['passt', 'passen schon'],
    ['fehlt', 'ohne Nachname oder Ort'], ['pruefen', 'neu zu prüfen'], ['uebersprungen', 'übersprungen'], ['fehler', 'Fehler'],
  ].filter(([status]) => zaehler[status]).map(([status, wort]) => `${zaehler[status]} ${wort}`).join(' · ');
}

/** Trockenlauf als CSV (Semikolon, mit BOM - so öffnet Excel die Umlaute richtig). */
function csvSpeichern(eintraege) {
  const feld = (wert) => `"${String(wert ?? '').replace(/"/g, '""')}"`;
  const zeilen = [
    ['Deal', 'Titel heute', 'Neuer Titel', 'Ergebnis', 'Details'].map(feld).join(';'),
    ...eintraege.map((e) => [e.dealId, e.alt, e.status === 'bereit' || e.status === 'umbenannt' ? e.neu : '', STATUS[e.status]?.text || e.status, e.grund].map(feld).join(';')),
  ];
  const adresse = URL.createObjectURL(new Blob([`﻿${zeilen.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }));
  const verweis = document.createElement('a');
  verweis.href = adresse;
  verweis.download = `Kundenbenennung-Trockenlauf-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(verweis);
  verweis.click();
  verweis.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 60000);
}

function Tabelle({ eintraege }) {
  const [alle, setAlle] = useState(false);
  const sichtbar = alle ? eintraege : eintraege.slice(0, ZEILEN_ANFANG);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', textAlign: 'left', color: '#64748B' }}>
              <th style={{ padding: '6px 8px' }}>Deal</th>
              <th style={{ padding: '6px 8px' }}>Titel heute</th>
              <th style={{ padding: '6px 8px' }}>Neuer Titel</th>
              <th style={{ padding: '6px 8px' }}>Ergebnis</th>
              <th style={{ padding: '6px 8px' }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {sichtbar.map((e) => (
              <tr key={e.dealId} style={{ borderTop: '1px solid #F1F5F9' }}>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: T.body }}>{e.dealId}</td>
                <td style={{ padding: '6px 8px', color: T.body }}>{e.alt}</td>
                <td style={{ padding: '6px 8px', color: T.ink, fontWeight: 600 }}>
                  {e.status === 'bereit' || e.status === 'umbenannt' ? e.neu : ''}
                </td>
                <td style={{ padding: '6px 8px' }}><Marke status={e.status} /></td>
                <td style={{ padding: '6px 8px', color: T.body }}>{e.grund}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {eintraege.length > ZEILEN_ANFANG ? (
        <button type="button" onClick={() => setAlle(!alle)}
          style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#E07000', fontFamily: 'inherit' }}>
          {alle ? 'Weniger zeigen' : `Alle ${eintraege.length} zeigen`}
        </button>
      ) : null}
    </div>
  );
}

export default function DealBenennungTest({ apiBase, session, onFertig }) {
  const [dealId, setDealId] = useState('');
  const [laeuft, setLaeuft] = useState('');
  const [einzel, setEinzel] = useState(null);
  const [plan, setPlan] = useState(null); // { gesamt, deals }
  const [lauf, setLauf] = useState(null); // { gesamt, fertig, abgebrochen, beendet }
  const [fehlerText, setFehlerText] = useState('');
  const [brichtAb, setBrichtAb] = useState(false);
  const abbruch = useRef(false);

  // Verlässt der Admin die Seite, hält der Lauf nach dem laufenden Paket an.
  useEffect(() => () => { abbruch.current = true; }, []);

  const senden = (body, timeout) => axios.post(
    `${apiBase}/automation/deal-benennung`,
    body,
    { headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}, timeout },
  );

  const testlauf = async () => {
    setLaeuft('test');
    setEinzel(null);
    setFehlerText('');
    try {
      const { data } = await senden({ op: 'test', dealId: dealId.trim() }, 60000);
      setEinzel(data.eintrag);
    } catch (error) {
      setFehlerText(error?.response?.data?.detail || error.message || 'Testlauf fehlgeschlagen.');
    } finally {
      setLaeuft('');
    }
  };

  const trockenlauf = async () => {
    setLaeuft('trocken');
    setFehlerText('');
    setPlan(null);
    setLauf(null);
    try {
      const { data } = await senden({ op: 'plan' }, 290000);
      setPlan({ gesamt: data.gesamt || 0, deals: data.deals || [] });
    } catch (error) {
      setFehlerText(error?.response?.data?.detail || error.message || 'Trockenlauf fehlgeschlagen.');
    } finally {
      setLaeuft('');
    }
  };

  const bereit = (plan?.deals || []).filter((e) => e.status === 'bereit');

  const umbenennen = async () => {
    if (!window.confirm(
      `${bereit.length} Deal(s) der Pipeline „Vertrieb“ bekommen jetzt den Titel aus dem Trockenlauf. `
      + 'Hat jemand einen Titel seither geändert oder käme heute etwas anderes heraus, bleibt der Deal und steht als „Neu prüfen“ da. Fortfahren?',
    )) return;
    abbruch.current = false;
    setBrichtAb(false);
    setLaeuft('echt');
    setFehlerText('');
    setLauf({ gesamt: bereit.length, fertig: 0, abgebrochen: false, beendet: false });
    const ergebnisse = [];
    try {
      // Pakete in der Reihenfolge des Plans (älteste Deals zuerst) - sonst bekäme ein
      // späterer Deal „Nachname - Ort“, das der Plan dem älteren zugedacht hat.
      for (let i = 0; i < bereit.length; i += PAKET) {
        if (abbruch.current) {
          setLauf((vorher) => ({ ...vorher, abgebrochen: true }));
          break;
        }
        const paket = bereit.slice(i, i + PAKET);
        let eintraege;
        try {
          const { data } = await senden({
            op: 'umbenennen', zeilen: paket.map(({ dealId: id, alt, neu }) => ({ dealId: id, alt, neu })),
          }, 280000);
          eintraege = data.eintraege || [];
        } catch (error) {
          const grund = error?.response?.data?.detail || error.message || 'Aufruf fehlgeschlagen.';
          eintraege = paket.map((e) => ({ ...e, status: 'fehler', grund }));
        }
        ergebnisse.push(...eintraege);
        const neuJeDeal = new Map(eintraege.map((e) => [String(e.dealId), e]));
        setPlan((vorher) => ({
          ...vorher,
          deals: vorher.deals.map((e) => (neuJeDeal.has(String(e.dealId)) ? { ...e, ...neuJeDeal.get(String(e.dealId)) } : e)),
        }));
        setLauf((vorher) => ({ ...vorher, fertig: Math.min(bereit.length, i + paket.length) }));
      }
    } finally {
      const z = zaehle(ergebnisse);
      // Ein Eintrag im Protokoll für den ganzen Lauf, nicht einer je Deal.
      await senden({
        op: 'bestand-ende', umbenannt: z.umbenannt || 0, pruefen: z.pruefen || 0, fehler: z.fehler || 0, abgebrochen: abbruch.current,
      }, 30000).catch(() => {});
      setLauf((vorher) => ({ ...vorher, beendet: true }));
      setLaeuft('');
      onFertig?.();
    }
  };

  const abbrechen = () => {
    abbruch.current = true;
    setBrichtAb(true);
  };

  const testBereit = Boolean(dealId.trim()) && !laeuft;
  const umbenennenFrei = bereit.length > 0 && !laeuft;
  const zaehler = zaehle(plan?.deals || []);
  // Erst was sich ändert oder Aufmerksamkeit braucht, dann der Rest.
  const reihenfolge = ['bereit', 'umbenannt', 'pruefen', 'fehler', 'fehlt', 'uebersprungen', 'passt'];
  const zeilen = (plan?.deals || []).filter((e) => e.status !== 'passt')
    .sort((a, b) => reihenfolge.indexOf(a.status) - reihenfolge.indexOf(b.status));
  const anteil = lauf?.gesamt ? Math.round((lauf.fertig / lauf.gesamt) * 100) : 100;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
          <strong style={{ color: T.ink }}>Einzelner Deal.</strong> Zeigt, wie der Deal heißen würde – ohne etwas in
          Bitrix zu ändern.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={dealId}
            onChange={(e) => setDealId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && testBereit) testlauf(); }}
            placeholder="Deal-ID, z. B. 11490"
            aria-label="Deal-ID für den Testlauf"
            style={{ ...feldStil, width: 200 }}
          />
          <button type="button" onClick={testlauf} disabled={!testBereit}
            style={{ ...knopfStil(true), opacity: testBereit ? 1 : 0.6 }}>
            <RefreshCw size={13} style={laeuft === 'test' ? { animation: 'spin 1s linear infinite' } : undefined} />
            {laeuft === 'test' ? 'Prüft …' : 'Testlauf'}
          </button>
        </div>
        {einzel ? (
          <div style={{ fontSize: T.tMeta, color: T.body, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Marke status={einzel.status} />
            <span>
              {einzel.status === 'bereit' ? <>„{einzel.alt}“ → <strong style={{ color: T.ink }}>„{einzel.neu}“</strong>. </> : (einzel.alt ? `„${einzel.alt}“: ` : '')}
              {einzel.grund}
            </span>
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid #F1F5F9', paddingTop: 14 }}>
        <div style={{ fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
          <strong style={{ color: T.ink }}>Bestand.</strong> Alle Deals der Pipeline „Vertrieb“. Der Trockenlauf zeigt,
          welcher Deal wie heißen würde – geändert wird dabei nichts. „Umbenennen“ ändert danach nur die Deals, die dort
          „Wird umbenannt“ waren. Bei gleichem „Nachname - Ort“ behält der älteste Deal den Namen ohne Zusatz.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={trockenlauf} disabled={Boolean(laeuft)}
            style={{ ...knopfStil(false), opacity: laeuft ? 0.6 : 1 }}>
            <RefreshCw size={13} style={laeuft === 'trocken' ? { animation: 'spin 1s linear infinite' } : undefined} />
            {laeuft === 'trocken' ? 'Plant … (kann einige Minuten dauern)' : 'Trockenlauf über alle'}
          </button>
          <button type="button" onClick={umbenennen} disabled={!umbenennenFrei}
            title={umbenennenFrei ? '' : 'Erst einen Trockenlauf starten'}
            style={{ ...knopfStil(true), opacity: umbenennenFrei ? 1 : 0.5 }}>
            <Play size={13} />
            {laeuft === 'echt' ? 'Benennt um …' : `Umbenennen${bereit.length ? ` (${bereit.length})` : ''}`}
          </button>
          {plan?.deals?.length ? (
            <button type="button" onClick={() => csvSpeichern(plan.deals)} disabled={laeuft === 'trocken'}
              style={{ ...knopfStil(false) }}>
              <Download size={13} /> Als CSV
            </button>
          ) : null}
          {laeuft === 'echt' ? (
            <button type="button" onClick={abbrechen} disabled={brichtAb}
              style={{ ...knopfStil(false), color: T.gefahr, borderColor: T.gefahrRand, opacity: brichtAb ? 0.6 : 1 }}>
              <Square size={12} />
              {brichtAb ? 'Hält nach diesem Paket an …' : 'Abbrechen'}
            </button>
          ) : null}
        </div>

        {lauf ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: T.tMeta, color: T.ink, fontWeight: 600 }}>
              <span>
                Umbenennen: {lauf.fertig} von {lauf.gesamt} bearbeitet
                {lauf.abgebrochen ? ' – abgebrochen' : (lauf.beendet ? ' – fertig' : '')}
              </span>
              <span style={{ color: T.body }}>{anteil} %</span>
            </div>
            <div role="progressbar" aria-valuemin={0} aria-valuemax={lauf.gesamt} aria-valuenow={lauf.fertig}
              aria-label={`${lauf.fertig} von ${lauf.gesamt} bearbeitet`}
              style={{ height: 8, background: '#E2E8F0', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                width: `${anteil}%`, height: '100%', borderRadius: 999, transition: 'width .3s ease',
                background: lauf.abgebrochen ? '#94A3B8' : '#E07000',
              }} />
            </div>
          </div>
        ) : null}

        {plan ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: T.tMeta, color: T.body }}>
              {plan.gesamt} Deal(s) in „Vertrieb“{plan.gesamt ? `: ${zusammenfassung(zaehler)}` : ''}
              {zaehler.passt ? ' (die passenden stehen nicht in der Liste, aber in der CSV)' : ''}
            </div>
            {zeilen.length ? <Tabelle eintraege={zeilen} /> : null}
          </div>
        ) : null}
      </div>

      {fehlerText ? <div style={{ fontSize: T.tMeta, color: T.gefahr }}>{fehlerText}</div> : null}
    </div>
  );
}
